/**
 * Select Field Component
 *
 * Renders a dropdown select for enum/select properties
 * Supports dynamic options loading via optionsSource:
 * - 'knowledgeBaseCollections': Loads KB collections
 * - 'dataTables': Loads data tables
 * - { type: 'nodeMethod', method: string, ... }: Loads options from a node method (for cascading dropdowns)
 *
 * For dynamic/large lists, uses a searchable combobox pattern for better UX.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { HelpCircle, Loader2, AlertCircle, Check, ChevronsUpDown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useRuntimeAvailability,
  isLanguageAvailable,
} from '@/hooks/use-runtime-availability';
import { knowledgeBaseApi, dataTablesApi, nodesApi } from '@/lib/api';
import { useWorkflowStore } from '@/stores/workflow-store';
import { cn } from '@/lib/utils';

/**
 * Extended optionsSource format for node method loading
 */
interface NodeMethodOptionsSource {
  type: 'nodeMethod';
  method: string;
  credentialKey?: string;       // Key in node properties containing credential ID
  dependsOn?: string[];         // Property keys this depends on (re-fetch when they change)
  parameters?: Record<string, string>;  // Parameter mapping, e.g., { "siteId": "{{siteId}}" }
}

type OptionsSourceType = string | NodeMethodOptionsSource;

interface SelectFieldProps {
  property: InferredPropertySchema;
  value: string | number;
  onChange: (value: string | number) => void;
  error?: string;
  // New props for cascading dropdown support
  nodeType?: string;
  nodeProperties?: Record<string, any>;
  credentialId?: string;
  onClearDependentFields?: (keys: string[]) => void;
  // Callback to update multiple fields at once (for template selectors, etc.)
  onBatchParameterChange?: (updates: Record<string, any>) => void;
}

export function SelectField({
  property,
  value,
  onChange,
  error,
  nodeType,
  nodeProperties,
  credentialId,
  onBatchParameterChange,
}: SelectFieldProps) {
  const [dynamicOptions, setDynamicOptions] = useState<{ value: string | number; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Track previous dependency values to detect changes
  const prevDepsRef = useRef<Record<string, any>>({});

  // Get workflow metadata to filter KB collections based on workflow location
  const workflowMetadata = useWorkflowStore((state) => state.currentWorkflowMetadata);

  // Get optionsSource from property (it may be on the schema property)
  const optionsSource = (property as any).optionsSource as OptionsSourceType | undefined;

  // Check if this is a nodeMethod type optionsSource (uses searchable combobox)
  const isNodeMethodSource = optionsSource &&
    typeof optionsSource === 'object' &&
    optionsSource.type === 'nodeMethod';

  // Use searchable combobox for dynamic sources with potentially large lists
  const useSearchableCombobox = isNodeMethodSource || optionsSource === 'knowledgeBaseCollections' || optionsSource === 'dataTables' || optionsSource === 'litellmModels' || optionsSource === 'litellmEmbeddingModels';

  // Serialize dependency values for proper change detection
  const serializedDeps = isNodeMethodSource
    ? JSON.stringify(
        ((optionsSource as NodeMethodOptionsSource).dependsOn || []).map(
          (key) => nodeProperties?.[key]
        )
      )
    : '';

  // Get dependency values for nodeMethod sources
  const getDependencyValues = useCallback((): Record<string, any> => {
    if (!isNodeMethodSource || !nodeProperties) return {};
    const source = optionsSource as NodeMethodOptionsSource;
    const deps: Record<string, any> = {};
    for (const depKey of source.dependsOn || []) {
      deps[depKey] = nodeProperties[depKey];
    }
    return deps;
  }, [isNodeMethodSource, optionsSource, nodeProperties]);

  // Interpolate parameter template strings with actual values
  const interpolateParameters = useCallback((params: Record<string, string> | undefined): Record<string, string> => {
    if (!params || !nodeProperties) return {};
    const result: Record<string, string> = {};
    for (const [key, template] of Object.entries(params)) {
      // Replace {{propertyKey}} with actual value
      const match = template.match(/^\{\{(\w+)\}\}$/);
      if (match) {
        const propKey = match[1];
        result[key] = String(nodeProperties[propKey] || '');
      } else {
        result[key] = template;
      }
    }
    return result;
  }, [nodeProperties]);

  // Fetch dynamic options when optionsSource is specified
  useEffect(() => {
    if (!optionsSource) return;

    const fetchOptions = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        if (optionsSource === 'knowledgeBaseCollections') {
          // Use filtered collections based on workflow location type
          // workflowLocationType: 0 = Personal, 1 = Team
          const isTeamWorkflow = workflowMetadata?.workflowLocationType === 1;
          const locationType: 'personal' | 'team' = isTeamWorkflow ? 'team' : 'personal';
          const teamFolderId = isTeamWorkflow ? workflowMetadata?.teamFolderId ?? undefined : undefined;

          const response = await knowledgeBaseApi.getCollectionsForWorkflow(locationType, teamFolderId);
          const collections = response.data?.collections || response.data || [];

          setDynamicOptions(
            collections.map((col: { id: string; name: string }) => ({
              value: col.id,
              label: col.name,
            }))
          );
        } else if (optionsSource === 'dataTables') {
          // Fetch data tables for selection
          const response = await dataTablesApi.getTablesForSelection();
          const tables = response.data || [];

          setDynamicOptions(
            tables.map((table: { id: string; name: string; description?: string; rowCount?: number }) => ({
              value: table.id,
              label: table.rowCount !== undefined ? `${table.name} (${table.rowCount} rows)` : table.name,
            }))
          );
        } else if (optionsSource === 'litellmModels' || optionsSource === 'litellmEmbeddingModels') {
          // Fetch available models from LiteLLM proxy (uses system credential)
          const endpoint = optionsSource === 'litellmEmbeddingModels' ? 'getLiteLLMEmbeddingModels' : 'getLiteLLMModels';
          const response = await (nodesApi as any)[endpoint]();
          const result = response.data;

          if (result?.success && result.options) {
            setDynamicOptions(
              result.options.map((opt: { value: string; label: string; description?: string }) => ({
                value: opt.value,
                label: opt.label,
              }))
            );
          } else {
            setLoadError(result?.error || 'Failed to load models');
            setDynamicOptions([]);
          }
        } else if (isNodeMethodSource) {
          // Fetch options from node method (for cascading dropdowns)
          const source = optionsSource as NodeMethodOptionsSource;

          // Check if we have the required nodeType
          if (!nodeType) {
            setLoadError('Node type not available');
            setDynamicOptions([]);
            return;
          }

          // Get credential ID from props or from node properties using the credentialKey
          let effectiveCredentialId = credentialId;
          if (!effectiveCredentialId && source.credentialKey && nodeProperties) {
            const credValue = nodeProperties[source.credentialKey];
            // Handle both string GUIDs and potential object values
            effectiveCredentialId = typeof credValue === 'string' ? credValue : credValue?.toString();
          }

          // Check if credential is required but not available
          if (!effectiveCredentialId) {
            setLoadError('Please select a credential first');
            setDynamicOptions([]);
            return;
          }

          // Check if all dependencies are satisfied
          // Note: Empty string values are considered valid (e.g., root folder)
          // Only undefined/null values are considered "missing"
          const depValues = getDependencyValues();
          const hasMissingDeps = (source.dependsOn || []).some(
            depKey => depValues[depKey] === undefined || depValues[depKey] === null
          );

          // Only skip fetch if there are truly missing deps (not just empty values)
          // Empty values are valid and should be passed to the backend
          if (hasMissingDeps) {
            // Don't fetch if dependencies aren't met - show placeholder instead
            setDynamicOptions([]);
            return;
          }

          // Interpolate parameters with current property values
          const params = interpolateParameters(source.parameters);

          // Call the API
          const response = await nodesApi.loadOptions(
            nodeType,
            source.method,
            effectiveCredentialId,
            params
          );

          const options = response.data?.options || [];
          setDynamicOptions(
            options.map((opt: { value: string; label: string } | { Value: string; Label: string }) => ({
              // Handle both camelCase and PascalCase from C#
              value: (opt as any).value ?? (opt as any).Value ?? '',
              label: (opt as any).label ?? (opt as any).Label ?? '',
            }))
          );
        }
      } catch (err: any) {
        console.error('Failed to fetch dynamic options:', err);
        setLoadError(err.response?.data?.error || err.message || 'Failed to load options');
        setDynamicOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [
    optionsSource,
    isNodeMethodSource,
    workflowMetadata?.workflowLocationType,
    workflowMetadata?.teamFolderId,
    nodeType,
    credentialId,
    // Watch specific credential key from nodeProperties for reactivity
    nodeProperties?.credentialId,
    // Watch serialized dependency values to detect changes
    serializedDeps,
    property.key,
    getDependencyValues,
    interpolateParameters
  ]);

  // Detect dependency changes and clear this field's value when parent changes
  useEffect(() => {
    if (!isNodeMethodSource) return;

    const source = optionsSource as NodeMethodOptionsSource;
    if (!source.dependsOn?.length || !nodeProperties) return;

    const currentDeps = getDependencyValues();
    const prevDeps = prevDepsRef.current;

    // Check if any dependency value changed
    const hasDepChanged = (source.dependsOn || []).some(
      depKey => prevDeps[depKey] !== undefined && prevDeps[depKey] !== currentDeps[depKey]
    );

    if (hasDepChanged && value) {
      // Clear this field's value when a parent dependency changes
      onChange('' as any);
    }

    // Update previous values reference
    prevDepsRef.current = currentDeps;
  }, [isNodeMethodSource, optionsSource, nodeProperties, getDependencyValues, value, onChange]);

  // Use dynamic options if optionsSource is specified, otherwise use static options
  const options = optionsSource ? dynamicOptions : (property.values || property.options || []);

  // Filter options based on search query (for combobox)
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt: { value: string | number; label: string }) =>
      opt.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Check runtime availability for language selectors
  const runtimeAvailability = useRuntimeAvailability();
  const isLanguageSelector = property.key === 'language' && options.some(
    (opt: { value: string | number }) => opt.value === 'python' || opt.value === 'javascript'
  );

  // Helper to check if an option should be disabled
  const isOptionDisabled = (optionValue: string | number): boolean => {
    if (!isLanguageSelector) return false;
    return !isLanguageAvailable(runtimeAvailability, String(optionValue));
  };

  // Helper to get display label with availability info
  const getOptionLabel = (option: { value: string | number; label: string }): string => {
    if (!isLanguageSelector) return option.label;
    if (isOptionDisabled(option.value)) {
      return `${option.label} (Not Available)`;
    }
    return option.label;
  };

  // Check if dependencies are waiting to be filled
  const hasPendingDependencies = (() => {
    if (!isNodeMethodSource || !nodeProperties) return false;
    const source = optionsSource as NodeMethodOptionsSource;
    return (source.dependsOn || []).some(depKey => !nodeProperties[depKey]);
  })();

  // Get a user-friendly placeholder based on state
  const getPlaceholder = (): string => {
    if (hasPendingDependencies && isNodeMethodSource) {
      const source = optionsSource as NodeMethodOptionsSource;
      const missingDeps = (source.dependsOn || []).filter(
        depKey => !nodeProperties?.[depKey]
      );
      if (missingDeps.length === 1) {
        return `Select ${missingDeps[0]} first`;
      }
      return `Select parent fields first`;
    }
    if (loadError) {
      return 'Error loading options';
    }
    return property.placeholder || 'Select an option';
  };

  // Get the selected option's label for display
  const selectedLabel = useMemo(() => {
    if (!value) return null;
    const selected = options.find((opt: { value: string | number }) =>
      opt.value.toString() === value.toString()
    );
    // Type guard to ensure selected has required properties
    if (selected && 'value' in selected && 'label' in selected && 
        selected.value !== undefined && selected.label !== undefined) {
      return getOptionLabel(selected as { value: string | number; label: string });
    }
    return null;
  }, [value, options, getOptionLabel]);

  // Get updatesField config from property (for template selectors, etc.)
  const updatesFieldConfig = (property as any).updatesField as {
    targetField: string;
    valueMap: Record<string, any>;
  } | undefined;

  // Helper function to generate a JSON schema template from data table columns
  const generateSchemaTemplate = useCallback((columns: Array<{ name: string; type: string; required?: boolean }>) => {
    const template: Record<string, string> = {};
    columns.forEach((col) => {
      // Skip system columns
      if (col.name.toLowerCase() === 'id') return;

      // Generate appropriate placeholder based on type
      let placeholder: string;
      switch (col.type.toLowerCase()) {
        case 'number':
        case 'integer':
        case 'decimal':
        case 'float':
          placeholder = '{{ $json.value }}';
          break;
        case 'boolean':
          placeholder = '{{ $json.flag }}';
          break;
        case 'datetime':
        case 'date':
          placeholder = '{{ $json.date }}';
          break;
        case 'json':
        case 'object':
        case 'array':
          placeholder = '{{ $json.data }}';
          break;
        default:
          placeholder = `{{ $json.${col.name} }}`;
      }
      template[col.name] = placeholder;
    });
    return JSON.stringify(template, null, 2);
  }, []);

  // Check if we should auto-populate schema template for data table selection
  const shouldPopulateSchemaTemplate = useMemo(() => {
    if (optionsSource !== 'dataTables') return false;
    if (property.key !== 'tableId') return false;
    const operation = nodeProperties?.operation;
    return ['insert', 'update', 'upsert'].includes(operation);
  }, [optionsSource, property.key, nodeProperties?.operation]);

  // Handle value change with optional field update
  const handleValueChange = async (selectedValue: string | number) => {
    // Check if we need to update another field based on this selection
    if (updatesFieldConfig && onBatchParameterChange) {
      const stringValue = String(selectedValue);
      const targetValue = updatesFieldConfig.valueMap[stringValue];
      if (targetValue !== undefined) {
        // Batch update both this field and the target field
        onBatchParameterChange({
          [property.key]: selectedValue,
          [updatesFieldConfig.targetField]: targetValue,
        });
        return;
      }
    }

    // Auto-populate schema template when selecting a data table for insert/update/upsert
    if (shouldPopulateSchemaTemplate && onBatchParameterChange && selectedValue) {
      try {
        const response = await dataTablesApi.getTable(String(selectedValue));
        const table = response.data;
        if (table?.schema?.columns) {
          const schemaTemplate = generateSchemaTemplate(table.schema.columns);
          onBatchParameterChange({
            [property.key]: selectedValue,
            data: schemaTemplate,
          });
          return;
        }
      } catch (err) {
        console.error('Failed to fetch table schema for template:', err);
        // Fall through to regular onChange
      }
    }

    // Regular onChange
    onChange(selectedValue);
  };

  // Handle value change for combobox
  const handleComboboxSelect = (selectedValue: string) => {
    if (isOptionDisabled(selectedValue)) return;

    // Only parse as number if it's a pure numeric string
    const isPureNumber = /^-?\d+(\.\d+)?$/.test(selectedValue);
    const finalValue = isPureNumber ? parseFloat(selectedValue) : selectedValue;

    handleValueChange(finalValue);
    setOpen(false);
    setSearchQuery('');
  };

  // Render searchable combobox for dynamic/large option lists
  const renderSearchableCombobox = () => (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            error || loadError ? 'border-destructive' : '',
            !value && "text-muted-foreground"
          )}
          disabled={isLoading || hasPendingDependencies}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading...</span>
            </div>
          ) : loadError ? (
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="truncate">{getPlaceholder()}</span>
            </div>
          ) : (
            <span className="truncate">
              {selectedLabel || getPlaceholder()}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Search ${property.label?.toLowerCase() || 'options'}...`}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {options.length === 0 ? 'No options available' : 'No matching options'}
            </CommandEmpty>
            <CommandGroup>
              {/* Show count when filtered */}
              {searchQuery && filteredOptions.length !== options.length && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground border-b">
                  Showing {filteredOptions.length} of {options.length} options
                </div>
              )}
              {/* Show total count when not filtered and has many options */}
              {!searchQuery && options.length > 20 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground border-b">
                  {options.length} options available — type to search
                </div>
              )}
              {filteredOptions.map((option: { value: string | number; label: string }) => (
                <CommandItem
                  key={option.value.toString()}
                  value={option.value.toString()}
                  onSelect={handleComboboxSelect}
                  disabled={isOptionDisabled(option.value)}
                  className={cn(
                    isOptionDisabled(option.value) && 'opacity-50'
                  )}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value?.toString() === option.value.toString()
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {getOptionLabel(option)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  // Render standard select for static/small option lists
  const renderStandardSelect = () => (
    <Select
      value={value?.toString()}
      onValueChange={(val) => {
        // Prevent selecting disabled options
        if (isOptionDisabled(val)) return;
        // Only parse as number if it's a pure numeric string (not a GUID or other ID)
        // GUIDs like "3fa85f64-..." would incorrectly parse as numbers with parseFloat
        const isPureNumber = /^-?\d+(\.\d+)?$/.test(val);
        const finalValue = isPureNumber ? parseFloat(val) : val;
        handleValueChange(finalValue);
      }}
      disabled={isLoading || hasPendingDependencies}
    >
      <SelectTrigger className={error || loadError ? 'border-destructive' : ''}>
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-muted-foreground">Loading...</span>
          </div>
        ) : loadError ? (
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-muted-foreground truncate">{getPlaceholder()}</span>
          </div>
        ) : (
          <SelectValue placeholder={getPlaceholder()} />
        )}
      </SelectTrigger>
      <SelectContent>
        {options.length === 0 && !isLoading && !hasPendingDependencies && (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            No options available
          </div>
        )}
        {options.map((option: { value: string | number; label: string }) => (
          <SelectItem
            key={option.value}
            value={option.value.toString()}
            disabled={isOptionDisabled(option.value)}
            className={isOptionDisabled(option.value) ? 'opacity-50' : ''}
          >
            {getOptionLabel(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={property.key}>
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {property.helpText && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-sm">{property.helpText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {property.description && (
        <p className="text-sm text-muted-foreground">{property.description}</p>
      )}

      {/* Use searchable combobox for dynamic sources, standard select for static */}
      {useSearchableCombobox ? renderSearchableCombobox() : renderStandardSelect()}

      {loadError && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-sm text-destructive flex items-center gap-1 cursor-help">
                <AlertCircle className="h-3 w-3" />
                Failed to load options
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">{loadError}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
